import React, { useEffect, useMemo, useState } from "react";
import { DownOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Dropdown, Input, Spin, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrganizations,
  switchOrganization,
} from "../redux/actions/organizationAction";
import { hydrateFromStorage } from "../redux/slices/organizationSlice";
import { getActiveOrganizationId, normalizeId } from "../utils/organizationId";
import CreateOrganizationModal from "./CreateOrganizationModal";

export default function OrgSwitcher() {
  const dispatch = useDispatch();
  const organizations = useSelector(
    (state) => state.organization.organizations,
  );
  const [switching, setSwitching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    dispatch(hydrateFromStorage());
    setLoading(true);
    dispatch(fetchOrganizations())
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch]);

  const storedOrgs = JSON.parse(localStorage.getItem("organizations") || "[]");
  const sourceOrgs = organizations.length ? organizations : storedOrgs;

  const orgList = useMemo(
    () =>
      sourceOrgs
        .map((org) => ({
          id: normalizeId(org.id),
          name: org.name || "Organization",
          role: org.role || "member",
        }))
        .filter((org) => org.id),
    [sourceOrgs],
  );

  const activeOrganizationId = getActiveOrganizationId();
  const activeOrg =
    orgList.find((org) => org.id === activeOrganizationId) || orgList[0];

  const filteredOrgs = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return orgList;
    return orgList.filter(
      (org) =>
        org.name.toLowerCase().includes(term) ||
        org.role.toLowerCase().includes(term),
    );
  }, [orgList, search]);

  const handleSwitch = (organizationId) => {
    const nextId = normalizeId(organizationId);
    if (!nextId || nextId === activeOrganizationId) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    dispatch(switchOrganization(nextId))
      .then((result) => {
        if (!result?.accessToken || !result?.user?.activeOrganizationId) {
          message.error(
            "Could not switch organization. Please try logging in again.",
            6,
          );
          setSwitching(false);
          return;
        }
        window.location.assign("/app");
      })
      .catch(() => {
        setSwitching(false);
      });
  };

  const triggerLabel = loading
    ? "Loading…"
    : activeOrg?.name || (orgList.length ? "Select organization" : "Create organization");

  const dropdown = (
    <div className="app-header-org-dropdown">
      {orgList.length > 3 ? (
        <div className="app-header-org-search">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Find an organization"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      ) : null}
      <div className="app-header-org-list">
        {loading || switching ? (
          <div className="app-header-org-loading">
            <Spin size="small" />
          </div>
        ) : filteredOrgs.length ? (
          filteredOrgs.map((org) => {
            const isActive = org.id === activeOrganizationId;
            return (
              <button
                key={org.id}
                type="button"
                data-organization-id={org.id}
                className={`app-header-org-option${isActive ? " is-active" : ""}`}
                onClick={() => handleSwitch(org.id)}
              >
                <span className="app-header-org-option-name">{org.name}</span>
                <span className="app-header-org-option-role">{org.role}</span>
              </button>
            );
          })
        ) : (
          <div className="app-header-org-empty">No organizations yet</div>
        )}
      </div>
      <div className="app-header-org-footer">
        <button
          type="button"
          className="app-header-org-create"
          onClick={() => {
            setOpen(false);
            setCreateOpen(true);
          }}
        >
          <PlusOutlined />
          <span>Create organization</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="app-header-org">
        <span className="app-header-org-label">Organization</span>
        <Dropdown
          open={open}
          onOpenChange={setOpen}
          trigger={["click"]}
          placement="bottomLeft"
          dropdownRender={() => dropdown}
        >
          <button
            type="button"
            className="app-header-org-trigger"
            data-testid="org-switcher-trigger"
            data-active-organization-id={activeOrganizationId || ""}
          >
            <span className="app-header-org-trigger-name">{triggerLabel}</span>
            <DownOutlined className="app-header-org-trigger-icon" />
          </button>
        </Dropdown>
      </div>
      <CreateOrganizationModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </>
  );
}
